import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  MapPin,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  MetricCard,
  LoadingState,
} from '@/components/common';
import { shipmentsApi, Shipment, ShipmentItem, ShipmentMilestone } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  BOOKED: 'bg-blue-100 text-blue-700',
  IN_TRANSIT: 'bg-amber-100 text-amber-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  DELAYED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

export default function ShipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [milestones, setMilestones] = useState<ShipmentMilestone[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [shipmentData, milestonesData] = await Promise.all([
        shipmentsApi.getById(id),
        shipmentsApi.getMilestones(id),
      ]);
      setShipment(shipmentData);
      setMilestones(milestonesData);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/shipments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!shipment) {
    return null;
  }

  const items = shipment.items || [];
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const itemColumns: Column<ShipmentItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{item.item?.name || '-'}</div>
            <div className="text-sm text-muted-foreground">{item.item?.sku}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (item) => (
        <span className="font-medium">{item.quantity} {item.item?.unit}</span>
      ),
    },
  ];

  const getMilestoneIcon = (milestone: string) => {
    const lower = milestone.toLowerCase();
    if (lower.includes('delivered') || lower.includes('arrived')) {
      return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    }
    if (lower.includes('delayed') || lower.includes('issue')) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    if (lower.includes('transit') || lower.includes('departed')) {
      return <Truck className="h-4 w-4 text-blue-500" />;
    }
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/shipments')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader
          title={shipment.shipmentNumber}
          description={shipment.trackingNumber ? `Tracking: ${shipment.trackingNumber}` : undefined}
          breadcrumbs={[
            { label: 'Shipments', href: '/shipments' },
            { label: shipment.shipmentNumber },
          ]}
          actions={
            <Button variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Add Milestone
            </Button>
          }
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Status"
          value={shipment.status.replace('_', ' ')}
          icon={<Truck className="h-5 w-5" />}
        />
        <MetricCard
          label="Items"
          value={totalItems}
          icon={<Package className="h-5 w-5" />}
        />
        <MetricCard
          label="Est. Arrival"
          value={formatDate(shipment.estimatedArrival)}
        />
        <MetricCard
          label="Milestones"
          value={milestones.length}
        />
      </div>

      {/* Route Info */}
      <Card>
        <CardHeader>
          <CardTitle>Route Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Origin</p>
                <p className="font-medium">{shipment.origin?.name || '-'}</p>
                <p className="text-sm text-muted-foreground">{shipment.origin?.code}</p>
              </div>
            </div>
            <div className="flex-1 px-8">
              <div className="flex items-center gap-2">
                <div className="h-0.5 flex-1 bg-muted" />
                <Truck className="h-5 w-5 text-muted-foreground" />
                <div className="h-0.5 flex-1 bg-muted" />
              </div>
              {shipment.carrier && (
                <p className="text-center text-sm text-muted-foreground mt-2">
                  {shipment.carrier}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-muted-foreground text-right">Destination</p>
                <p className="font-medium text-right">{shipment.destination?.name || '-'}</p>
                <p className="text-sm text-muted-foreground text-right">{shipment.destination?.code}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <MapPin className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Shipment Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Shipment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Status</span>
              <Badge className={statusColors[shipment.status]}>
                {shipment.status.replace('_', ' ')}
              </Badge>
            </div>
            <Separator />
            {shipment.carrier && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Carrier</span>
                <span>{shipment.carrier}</span>
              </div>
            )}
            {shipment.trackingNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tracking #</span>
                <span className="font-mono text-sm">{shipment.trackingNumber}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. Departure</span>
              <span>{formatDate(shipment.estimatedDeparture)}</span>
            </div>
            {shipment.actualDeparture && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Departure</span>
                <span>{formatDate(shipment.actualDeparture)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. Arrival</span>
              <span>{formatDate(shipment.estimatedArrival)}</span>
            </div>
            {shipment.actualArrival && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual Arrival</span>
                <span>{formatDate(shipment.actualArrival)}</span>
              </div>
            )}
            {(shipment.totalWeight || shipment.totalVolume) && (
              <>
                <Separator />
                {shipment.totalWeight && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Weight</span>
                    <span>{shipment.totalWeight} kg</span>
                  </div>
                )}
                {shipment.totalVolume && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Volume</span>
                    <span>{shipment.totalVolume} m³</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Shipment Timeline</CardTitle>
            <CardDescription>Track progress and milestones</CardDescription>
          </CardHeader>
          <CardContent>
            {milestones.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No milestones yet</p>
                <p className="text-muted-foreground">Milestones will appear here as the shipment progresses.</p>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-muted" />
                <div className="space-y-6">
                  {milestones.map((milestone) => (
                    <div key={milestone.id} className="relative flex gap-4 pl-10">
                      <div className="absolute left-2 flex h-5 w-5 items-center justify-center rounded-full bg-background border-2 border-muted">
                        {getMilestoneIcon(milestone.milestone)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{milestone.milestone}</p>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(milestone.timestamp)}
                          </span>
                        </div>
                        {milestone.description && (
                          <p className="text-sm text-muted-foreground">{milestone.description}</p>
                        )}
                        {milestone.location && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <MapPin className="h-3 w-3" />
                            {milestone.location}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle>Shipment Items</CardTitle>
          <CardDescription>{items.length} item(s) in this shipment</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={items}
            columns={itemColumns}
            emptyTitle="No items"
            emptyDescription="No items have been added to this shipment."
          />
        </CardContent>
      </Card>
    </div>
  );
}
